import type { FirebaseEnvConfig } from '#firebase/types.js';

import { z } from 'zod';

import { zEnvBoolean } from '@elara/core/zod-utils';

import app from '#app.json' with { type: 'json' };

/**
 * Shared env/config contract (ELARA-694).
 *
 * This module is the **platform-neutral** half of the env seam: it owns the
 * schema, the derived {@link Env} type, and the pure {@link parseEnv} transform.
 * It deliberately does **not** read `import.meta.env` (or any host global), so
 * it is safe to import under Metro/Hermes where `import.meta.env` is `undefined`
 * — touching it during module load crashes the bundle.
 *
 * Each platform supplies the raw values through a {@link ReadRawEnv} provider and
 * feeds them to {@link parseEnv}:
 * - Web reads Vite's `import.meta.env` (see `env.ts`, the web provider).
 * - React Native reads Expo's `Constants.expoConfig.extra` (its own composition root).
 */

/**
 * Raw, untrusted env key/value map as read from the host platform, before
 * validation. Parsed into the trusted {@link Env} contract by {@link parseEnv}.
 */
export type RawEnv = Record<string, unknown>;

/** A platform-specific provider of the raw env map. Owns any read side-effects. */
export type ReadRawEnv = () => RawEnv;

const EnvSchema = z.object({
  VITE_DEBUG_MODE: zEnvBoolean.default(false),
  VITE_USE_FIREBASE_EMULATORS: zEnvBoolean.default(false),
  VITE_FIREBASE_AUTH_PORT: z.coerce.number().int().positive().default(23598),
  VITE_FIREBASE_FIRESTORE_PORT: z.coerce.number().int().positive().default(23580),
  VITE_FIREBASE_STORAGE_PORT: z.coerce.number().int().positive().default(23599),
  VITE_FIREBASE_HOSTING_PORT: z.coerce.number().int().positive().default(23500),
  /**
   * Where the emulators are reachable FROM the app. Web always answers `127.0.0.1`, because the
   * browser runs on the same machine; a mobile app does not, so the host is a per-device setting —
   * `127.0.0.1` on the iOS simulator (it shares the Mac's loopback), `10.0.2.2` on the Android
   * emulator (its alias for the host), and the Mac's LAN address on a physical device.
   */
  VITE_FIREBASE_EMULATOR_HOST: z.string().default('127.0.0.1'),
  VITE_CAPTURE_KEY: z.string().optional(),
  VITE_CAPTURE_MODE: z.enum(['extended', 'basic']).default('extended'),
  VITE_ENABLE_WHY_DID_YOU_RENDER: zEnvBoolean.default(false),
  VITE_GOOGLE_MAPS_API_KEY: z.string().optional(), // Google will block requests from non-Elara domains
  VITE_GOOGLE_MAPS_MAP_ID: z.string().optional(),
  // Native Google sign-in OAuth web client id (ELARA-728). Mobile-only: web runs both Google and Apple
  // through Firebase's `signInWithPopup`, so it needs no client id. Required on ANDROID (GoogleSignin
  // needs the web client id to mint a Firebase-acceptable ID token); on iOS it's optional — the SDK
  // reads the iOS client from the bundled GoogleService-Info.plist. PUBLIC (ships in the app bundle,
  // restricted by bundle id / SHA fingerprint). Injected via EAS env.
  VITE_GOOGLE_WEB_CLIENT_ID: z.string().optional(),
  VITE_IMAGE_SEARCH_API_URL: z.string().optional(),
  VITE_IMAGE_SEARCH_API_KEY: z.string().optional(),
  // Must be an ABSOLUTE Cloud Run URL: mobile has no same-origin to fall back to, and web
  // preview deployments call the backend cross-origin. `.url()` rejects relative paths.
  VITE_FLOW_API_BASE_URL: z.string().url().optional(),
  VITE_FIREBASE_VAPID_KEY: z.string().optional(),
  // Stripe publishable key (pk_test_… / pk_live_…). PUBLIC by design — it ships in the bundle and
  // only mints client-side tokens; the secret key never leaves the server. Unset ⇒ the membership
  // payment form cannot mount, so signup card capture is unavailable (billing dormant).
  VITE_STRIPE_PUBLISHABLE_KEY: z.string().optional(),
  VITE_SENTRY_ENABLED: zEnvBoolean.default(false),
  VITE_SENTRY_DSN: z.string().optional(),
  VITE_SENTRY_SPOTLIGHT: zEnvBoolean.default(false),
  VITE_SENTRY_SPOTLIGHT_URL: z.string().optional(),
  SENTRY_SPOTLIGHT: zEnvBoolean.optional(),
  SENTRY_SPOTLIGHT_URL: z.string().optional(),
  ENDPOINT_SUFFIX: z.string().optional(),
  MODE: z.enum(['development', 'production', 'test']).optional(),
  GITHUB_REF_NAME_SLUG: z.string().optional(),
  GITHUB_SHA: z.string().optional(),
  BASE_URL: z.string().default('http://localhost:5173'),
  DEV: zEnvBoolean.default(false),
  VITEST: zEnvBoolean.default(false),
  APP_NAME: z.string().default(app.defaults.appName),
  APP_VERSION: z.string().default('0.0.0'),
  FIREBASE: z.object({
    apiKey: z.string(),
    authDomain: z.string(),
    projectId: z.string(),
    storageBucket: z.string(),
    messagingSenderId: z.string().optional(),
    appId: z.string(),
    measurementId: z.string().optional(),
    productionRegion: z.string().optional(),
  }),
});

export { EnvSchema };

export type Env = z.output<typeof EnvSchema>;

/**
 * Resolves the Firebase web config from the raw env, supporting either a single
 * JSON blob (`VITE_FIREBASE_JSON`) or discrete `VITE_FIREBASE_*` fields.
 */
function dynamicFirebaseConfig(raw: RawEnv): FirebaseEnvConfig | undefined {
  if (typeof raw.VITE_FIREBASE_JSON === 'string' && raw.VITE_FIREBASE_JSON) {
    return JSON.parse(raw.VITE_FIREBASE_JSON) as FirebaseEnvConfig;
  } else if (raw.VITE_FIREBASE_API_KEY) {
    return {
      apiKey: raw.VITE_FIREBASE_API_KEY as string,
      authDomain: raw.VITE_FIREBASE_AUTH_DOMAIN as string,
      projectId: raw.VITE_FIREBASE_PROJECT_ID as string,
      storageBucket: raw.VITE_FIREBASE_STORAGE_BUCKET as string,
      messagingSenderId: raw.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
      appId: raw.VITE_FIREBASE_APP_ID as string,
      measurementId: raw.VITE_FIREBASE_MEASUREMENT_ID as string,
      region: raw.VITE_FIREBASE_REGION as string,
    };
  } else {
    return;
  }
}

/**
 * Parses and validates a raw env map into the trusted {@link Env} contract. Pure:
 * no host globals, no side-effects — safe to call on any platform.
 */
export function parseEnv(raw: RawEnv): Env {
  return EnvSchema.parse({
    ...raw,
    MODE: raw.NODE_ENV,
    APP_VERSION: raw.APP_VERSION,
    VITE_USE_FIREBASE_EMULATORS: raw.VITE_USE_FIREBASE_EMULATORS,
    FIREBASE: dynamicFirebaseConfig(raw),
  });
}

/**
 * Platform-injected env holder (ELARA-700).
 *
 * Shared, platform-agnostic modules (signal stores, DI containers, data utils) must not import the
 * web `Env` *value* from `client/src/env.ts` — that reads `import.meta.env` at module load and
 * crashes under Metro/Hermes. Instead each platform composes its `Env` (web from `import.meta.env`,
 * React Native from `expo-constants`) and injects it once at bootstrap via {@link setEnv}; shared
 * code reads it lazily through {@link getEnv}. Mirrors the `ErrorReporter` holder pattern.
 */
let currentEnv: Env | undefined;

/** Injects the active env (call once at app bootstrap, before any store reads it). */
export function setEnv(env: Env): void {
  currentEnv = env;
}

/** Returns the injected env. Throws if a shared module reads it before bootstrap called {@link setEnv}. */
export function getEnv(): Env {
  if (!currentEnv) {
    throw new Error('[env] getEnv() called before setEnv(); the platform must initialize Env at bootstrap.');
  }
  return currentEnv;
}
